/**
 * What a detail page (story hub, audio editor, ready screen) should render for
 * its main record.
 *
 * The readers (`getStory`, `getAudioStory`) answer `null` when the record does
 * not exist OR belongs to another account — one answer, so the page cannot leak
 * that a foreign id exists — and THROW only on a genuine failure (network, 5xx,
 * DB). The two must not share a screen: "retry" on a story that is not yours
 * can never succeed.
 *
 *  - `loading`     — no data yet and no error: skeleton.
 *  - `error`       — a real, transient failure: "couldn't load" + retry.
 *  - `unavailable` — resolved to nothing: not-found / no-access screen.
 *  - `ready`       — render the record.
 */
export type DetailState = 'loading' | 'error' | 'unavailable' | 'ready';

export function resolveDetailState<T>(q: {
  data: T | null | undefined;
  error: unknown;
  isLoading: boolean;
}): DetailState {
  if (q.data) return 'ready';
  if (q.error) return 'error';
  if (q.isLoading) return 'loading';
  // `null` (resolved: missing / not yours) — or `undefined` with nothing in
  // flight (query disabled: no id at all), which is the same thing to a viewer.
  return 'unavailable';
}
